import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, ArrowDown } from "lucide-react";
import { FadeUp, PageShell, Eyebrow, SectionHeading } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function Turnkey() {
  usePageTitle("Turnkey Bungalow Construction in Maharashtra — Sai Leela Decor");
  const c = useContent();

  return (
    <PageShell testid="turnkey-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-40 pb-20">
        <FadeUp>
          <Eyebrow className="mb-5">Turnkey Bungalow Construction</Eyebrow>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08] text-bone max-w-4xl">
            Turnkey Bungalow Construction in <span className="italic text-bronze">Maharashtra</span>
          </h1>
          <p className="mt-7 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-stone2">
            Sai Leela Decor provides complete turnkey bungalow construction and interior
            execution — from architectural planning and civil construction to premium
            interiors, landscaping and final handover. One team. One responsibility.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-5">
            <Link to="/contact" data-testid="turnkey-cta-btn" className="inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-8 py-4 transition-all duration-500 hover:bg-bronze-light">
              Start Your Bungalow Project <ArrowRight size={14} />
            </Link>
            <Link to="/projects" data-testid="turnkey-projects-link" className="link-underline inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-bone/80 hover:text-bronze transition-colors duration-500 py-4">
              View Our Projects <ArrowUpRight size={14} />
            </Link>
          </div>
        </FadeUp>
      </section>

      <section data-testid="turnkey-scope-section" className="border-y border-white/[0.07] bg-ink/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <SectionHeading eyebrow="What We Handle" title="Every trade, every stage, one roof" className="mb-14" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-white/[0.07] border border-white/[0.07]">
            {c.turnkeyServices.map((s, i) => (
              <FadeUp key={s} delay={0.03 * i} className="bg-obsidian p-6 flex items-center gap-4 transition-colors duration-500 hover:bg-bronze/[0.05]">
                <span className="font-mono text-[10px] text-bronze shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-sm font-light text-bone/85 tracking-wide">{s}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="quality-stages-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <SectionHeading eyebrow="Construction Quality" title="Real construction, stage by stage" className="mb-6" />
        <FadeUp>
          <p className="text-base font-light leading-relaxed text-stone2 max-w-2xl mb-14">
            We are builders first. These are the stages your bungalow passes through —
            supervised, photographed and quality-checked before the finishes begin.
          </p>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {c.qualityStages.map((q, i) => (
            <FadeUp key={q.title} delay={0.04 * i}>
              <div data-testid={`quality-stage-${i}`} className="group border border-white/[0.07] transition-colors duration-500 hover:border-bronze/40">
                <div className="overflow-hidden">
                  <img src={q.image} alt={q.title} className="w-full h-auto transition-transform duration-700 ease-luxury group-hover:scale-105" />
                </div>
                <div className="flex items-center gap-4 p-5">
                  <span className="font-mono text-[10px] text-bronze">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm font-light text-bone/85 tracking-wide">{q.title}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      <section data-testid="process-section" className="border-y border-white/[0.07] bg-ink/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <SectionHeading eyebrow="The Process" title="Ten steps from plot to possession" className="mb-14" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16">
            {c.process.map((p, i) => (
              <FadeUp key={p.num} delay={0.04 * i} className="group border-b border-white/[0.07] py-8">
                <div className="flex items-start gap-6">
                  <span className="font-serif text-2xl font-light text-bronze shrink-0 w-12">{p.num}</span>
                  <div>
                    <h3 className="font-serif text-xl sm:text-2xl font-light text-bone transition-colors duration-500 group-hover:text-bronze">{p.title}</h3>
                    <p className="mt-2 text-sm font-light leading-relaxed text-stone2 max-w-md">{p.text}</p>
                  </div>
                </div>
                {i < c.process.length - 1 && <ArrowDown size={12} className="text-bronze/40 ml-5 mt-3" />}
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="turnkey-why-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <SectionHeading eyebrow="Why Sai Leela Decor" title="Why Choose Sai Leela Decor?" className="mb-14" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.07] border border-white/[0.07]">
          {c.whyUs.map((w, i) => (
            <FadeUp key={w.num} delay={0.05 * i} className="bg-obsidian p-7 transition-colors duration-500 hover:bg-bronze/[0.05]">
              <p className="font-mono text-xs text-bronze mb-5">{w.num}</p>
              <h3 className="font-serif text-lg sm:text-xl font-light text-bone leading-snug">{w.title}</h3>
              <p className="mt-3 text-sm font-light leading-relaxed text-stone2">{w.text}</p>
            </FadeUp>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.07] bg-ink/40 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_100%,rgba(197,160,89,0.1),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center relative">
          <FadeUp>
            <Eyebrow className="mb-6">From Plot to Possession</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-bone max-w-2xl mx-auto leading-snug">
              Give us your plot and requirements. We take care of the rest.
            </h2>
            <Link to="/contact" data-testid="turnkey-bottom-cta" className="mt-10 inline-flex items-center gap-3 bg-bronze text-obsidian text-[11px] uppercase tracking-[0.25em] px-10 py-5 transition-all duration-500 hover:bg-bronze-light">
              Get a Free Consultation <ArrowRight size={14} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </PageShell>
  );
}
